import { DeleteOutlined } from '@ant-design/icons';
import { Button, Select, Space } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
// no React import needed with automatic JSX runtime
import type { TFunction } from 'i18next';
import WebGrid from '../../../webgrid/src';
import type { IncomingMetal, IncomingMetalReport } from '../../domain/metals';
import IncomingSummary from './IncomingSummary';

type Props = {
    t: TFunction;
    dates: string[];
    selectedDate: string | null;
    onDateChange: (value: Dayjs | null) => void;
    onAddToInventory: () => void;
    onDelete: (row: Record<string, unknown>) => void;
    data: IncomingMetal[];
    loading: boolean;
    summary: IncomingMetalReport | null;
};

function IncomingContent({
    t,
    dates,
    selectedDate,
    onDateChange,
    onAddToInventory,
    onDelete,
    data,
    loading,
    summary,
}: Readonly<Props>) {
    const format2 = (val: number | string): string => Number(val ?? 0).toFixed(2);
    // Helper for numeric columns with identical renderer
    const numericCol = (key: string, title: string, dataIndex: string) => ({
        key,
        title,
        dataIndex,
        render: (val: number | string) => format2(val),
    });

    const columns = [
        { key: 'contractNumber', title: t('metals.contract'), dataIndex: 'contract_number' },
        { key: 'client', title: t('metals.client'), dataIndex: 'client_name' },
        { key: 'tipmetal', title: t('metals.metal'), dataIndex: 'metal_type' },
        numericCol('karat', t('metals.karat'), 'purity'),
        numericCol('cantitate', t('metals.qty'), 'quantity_grams'),
        numericCol('sumainlei', t('metals.valueRon'), 'value_ron'),
        numericCol('gr24k', t('metals.gr24k'), 'gr24k'),
        {
            key: 'actions',
            title: t('clients.actions'),
            render: (_val: unknown, row: Record<string, unknown>) => (
                <Space size="small">
                    <Button
                        icon={<DeleteOutlined />}
                        danger
                        size="small"
                        onClick={e => {
                            e.stopPropagation();
                            onDelete(row);
                        }}
                    />
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <Select
                        value={selectedDate ?? undefined}
                        onChange={value => onDateChange(value ? dayjs(value) : null)}
                        style={{ width: '160px' }}
                        placeholder={t('metals.selectDate')}
                    >
                        {dates.map(d => (
                            <Select.Option key={d} value={d}>
                                {dayjs(d).format('YYYY-MM-DD')}
                            </Select.Option>
                        ))}
                    </Select>
                    <Button type="primary" onClick={onAddToInventory} disabled={!selectedDate}>
                        {t('metals.addToStock')}
                    </Button>
                </div>
            </div>
            {/* eslint-disable @typescript-eslint/no-explicit-any */}
            <WebGrid
                data={data as any}
                columns={columns as any}
                loading={loading}
                pagination={false}
                onRowClick={() => {}}
                onRowDoubleClick={() => {}}
                onExport={() => {}}
                height={400}
            />
            {/* eslint-enable @typescript-eslint/no-explicit-any */}
            {summary && <IncomingSummary t={t} groups={summary.grouped} />}
        </div>
    );
}

export default IncomingContent;
